import { useEffect, useState } from "react";
import {
  ApiError,
  fetchEngagementAutoDisableThresholds,
  fetchPlatformStockAlertCaps,
  fetchPlatformSupportNotificationEmails,
  postEngagementAutoDisableThresholds,
  postPlatformStockAlertCaps,
  postPlatformSupportNotificationEmails,
  STOCK_TYPE_LABELS,
  STOCK_TYPES,
  type EngagementAutoDisableThresholds,
  type StockAlertCaps,
  type StockType,
} from "../lib/api";
import { isPlatformAdmin } from "../lib/auth";
import { useDocumentTitle } from "../hooks/use-document-title";

type CapDraft = Record<StockType, { unlimited: boolean; value: string }>;
type ThresholdsDraft = { zeroDeliveryDays: string; zeroReadDays: string };

function toThresholdsDraft(thresholds: EngagementAutoDisableThresholds): ThresholdsDraft {
  return { zeroDeliveryDays: String(thresholds.zeroDeliveryDays), zeroReadDays: String(thresholds.zeroReadDays) };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toDraft(caps: StockAlertCaps): CapDraft {
  const draft = {} as CapDraft;
  for (const stockType of STOCK_TYPES) {
    const cap = caps[stockType];
    draft[stockType] = { unlimited: cap === null, value: cap === null ? "" : String(cap) };
  }
  return draft;
}

function NoAccess() {
  return (
    <div>
      <h1 className="page-title">Platform Admin</h1>
      <div className="card">
        <div className="empty-state">You don't have permission to view this page.</div>
      </div>
    </div>
  );
}

export function PlatformAdminPage() {
  useDocumentTitle("Platform Admin");
  const isAdmin = isPlatformAdmin();
  const [draft, setDraft] = useState<CapDraft | null>(null);
  const [loading, setLoading] = useState(isAdmin);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [emailsDraft, setEmailsDraft] = useState<string | null>(null);
  const [emailsError, setEmailsError] = useState<string | null>(null);
  const [emailsSaving, setEmailsSaving] = useState(false);
  const [emailsSaved, setEmailsSaved] = useState(false);
  const [thresholdsDraft, setThresholdsDraft] = useState<ThresholdsDraft | null>(null);
  const [thresholdsError, setThresholdsError] = useState<string | null>(null);
  const [thresholdsSaving, setThresholdsSaving] = useState(false);
  const [thresholdsSaved, setThresholdsSaved] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    Promise.all([
      fetchPlatformStockAlertCaps(),
      fetchPlatformSupportNotificationEmails(),
      fetchEngagementAutoDisableThresholds(),
    ])
      .then(([caps, emails, thresholds]) => {
        if (cancelled) return;
        setDraft(toDraft(caps));
        setEmailsDraft(emails.join("\n"));
        setThresholdsDraft(toThresholdsDraft(thresholds));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          setForbidden(true);
        } else {
          setError(err instanceof Error ? err.message : "Failed to load platform settings");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  if (!isAdmin || forbidden) return <NoAccess />;

  function updateRow(stockType: StockType, next: Partial<CapDraft[StockType]>) {
    setSaved(false);
    setDraft((prev) => (prev ? { ...prev, [stockType]: { ...prev[stockType], ...next } } : prev));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!draft) return;
    setError(null);
    setSaved(false);

    const caps = {} as StockAlertCaps;
    for (const stockType of STOCK_TYPES) {
      const row = draft[stockType];
      if (row.unlimited) {
        caps[stockType] = null;
        continue;
      }
      const parsed = Number(row.value);
      if (row.value.trim() === "" || !Number.isInteger(parsed) || parsed < 0) {
        setError(`${STOCK_TYPE_LABELS[stockType]}: enter a whole number of 0 or more, or tick Unlimited`);
        return;
      }
      caps[stockType] = parsed;
    }

    setSaving(true);
    try {
      const result = await postPlatformStockAlertCaps(caps);
      setDraft(toDraft(result));
      setSaved(true);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
      } else {
        setError(err instanceof Error ? err.message : "Failed to save platform settings");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveEmails(e: React.FormEvent) {
    e.preventDefault();
    if (emailsDraft === null) return;
    setEmailsError(null);
    setEmailsSaved(false);

    const emails = emailsDraft
      .split(/[\n,]/)
      .map((entry) => entry.trim())
      .filter((entry) => entry !== "");
    const invalid = emails.find((entry) => !EMAIL_PATTERN.test(entry));
    if (invalid) {
      setEmailsError(`"${invalid}" is not a valid email address`);
      return;
    }

    setEmailsSaving(true);
    try {
      const result = await postPlatformSupportNotificationEmails(emails);
      setEmailsDraft(result.join("\n"));
      setEmailsSaved(true);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
      } else {
        setEmailsError(err instanceof Error ? err.message : "Failed to save platform settings");
      }
    } finally {
      setEmailsSaving(false);
    }
  }

  async function handleSaveThresholds(e: React.FormEvent) {
    e.preventDefault();
    if (!thresholdsDraft) return;
    setThresholdsError(null);
    setThresholdsSaved(false);

    const parseDays = (label: string, value: string): number | null => {
      const parsed = Number(value);
      if (value.trim() === "" || !Number.isInteger(parsed) || parsed < 1) {
        setThresholdsError(`${label}: enter a whole number of 1 or more`);
        return null;
      }
      return parsed;
    };
    const zeroDeliveryDays = parseDays("Zero-delivery day threshold", thresholdsDraft.zeroDeliveryDays);
    if (zeroDeliveryDays === null) return;
    const zeroReadDays = parseDays("Zero-read day threshold", thresholdsDraft.zeroReadDays);
    if (zeroReadDays === null) return;

    setThresholdsSaving(true);
    try {
      const result = await postEngagementAutoDisableThresholds({ zeroDeliveryDays, zeroReadDays });
      setThresholdsDraft(toThresholdsDraft(result));
      setThresholdsSaved(true);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
      } else {
        setThresholdsError(err instanceof Error ? err.message : "Failed to save platform settings");
      }
    } finally {
      setThresholdsSaving(false);
    }
  }

  return (
    <div>
      <h1 className="page-title">Platform Admin</h1>

      {loading && <div className="loading">Loading platform settings...</div>}
      {!loading && !draft && error && <div className="error-msg">{error}</div>}

      {draft && (
        <form className="card" style={{ maxWidth: 560 }} onSubmit={(e) => void handleSave(e)}>
          <div className="card-header">
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Daily stock alert caps</h2>
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
            Default maximum stock alerts each dealer receives per day, per stock type. Individual dealers can
            override these on their profile page.
          </div>

          <table>
            <thead>
              <tr>
                <th>Stock type</th>
                <th>Daily cap</th>
                <th>Unlimited</th>
              </tr>
            </thead>
            <tbody>
              {STOCK_TYPES.map((stockType) => {
                const row = draft[stockType];
                return (
                  <tr key={stockType}>
                    <td>{STOCK_TYPE_LABELS[stockType]}</td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={row.value}
                        disabled={row.unlimited || saving}
                        onChange={(e) => updateRow(stockType, { value: e.target.value })}
                        style={{ width: 100 }}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={row.unlimited}
                        disabled={saving}
                        onChange={(e) => updateRow(stockType, { unlimited: e.target.checked })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {error && <div className="error-msg" style={{ marginTop: 12 }}>{error}</div>}

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            {saved && <span className="badge badge-success">Saved</span>}
          </div>
        </form>
      )}

      {emailsDraft !== null && (
        <form className="card" style={{ maxWidth: 560 }} onSubmit={(e) => void handleSaveEmails(e)}>
          <div className="card-header">
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Support ticket notification emails</h2>
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
            Email addresses to notify when a dealer raises a new support ticket, one per line. Leave empty to
            disable.
          </div>

          <div className="form-group">
            <textarea
              value={emailsDraft}
              disabled={emailsSaving}
              rows={5}
              placeholder="support@example.com"
              onChange={(e) => {
                setEmailsSaved(false);
                setEmailsDraft(e.target.value);
              }}
            />
          </div>

          {emailsError && <div className="error-msg" style={{ marginTop: 12 }}>{emailsError}</div>}

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={emailsSaving}>
              {emailsSaving ? "Saving…" : "Save"}
            </button>
            {emailsSaved && <span className="badge badge-success">Saved</span>}
          </div>
        </form>
      )}

      {thresholdsDraft && (
        <form className="card" style={{ maxWidth: 560 }} onSubmit={(e) => void handleSaveThresholds(e)}>
          <div className="card-header">
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Engagement auto-disable thresholds</h2>
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
            A dealer's automated stock-alert notifications (saved-search matches, not ad-hoc personalised
            notifications) are turned off automatically once their consecutive-day streak of 0% delivered, or
            0% read, reaches the relevant threshold below.
          </div>

          <div className="form-group">
            <label>Zero-delivery day threshold</label>
            <input
              type="number"
              min={1}
              step={1}
              value={thresholdsDraft.zeroDeliveryDays}
              disabled={thresholdsSaving}
              onChange={(e) => {
                setThresholdsSaved(false);
                setThresholdsDraft((prev) => (prev ? { ...prev, zeroDeliveryDays: e.target.value } : prev));
              }}
              style={{ width: 100 }}
            />
          </div>
          <div className="form-group">
            <label>Zero-read day threshold</label>
            <input
              type="number"
              min={1}
              step={1}
              value={thresholdsDraft.zeroReadDays}
              disabled={thresholdsSaving}
              onChange={(e) => {
                setThresholdsSaved(false);
                setThresholdsDraft((prev) => (prev ? { ...prev, zeroReadDays: e.target.value } : prev));
              }}
              style={{ width: 100 }}
            />
          </div>

          {thresholdsError && <div className="error-msg" style={{ marginTop: 12 }}>{thresholdsError}</div>}

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={thresholdsSaving}>
              {thresholdsSaving ? "Saving…" : "Save"}
            </button>
            {thresholdsSaved && <span className="badge badge-success">Saved</span>}
          </div>
        </form>
      )}
    </div>
  );
}
